import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    gold: Palette['primary'];
  }
  interface PaletteOptions {
    gold?: PaletteOptions['primary'];
  }
}

export const urbanEleganceTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main:        '#0D1F3C',
      light:       '#2C4A80',
      dark:        '#070F1E',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main:        '#C9A86C',
      light:       '#E8CFA0',
      dark:        '#A08040',
      contrastText: '#FFFFFF',
    },
    error: {
      main: '#FF6B35',  // URGENT — 기존 유지
    },
    warning: {
      main: '#C9A86C',
    },
    background: {
      default: '#F5F7FA',
      paper:   '#FFFFFF',
    },
    text: {
      primary:   '#0D1F3C',
      secondary: '#4A5A72',
      disabled:  '#8896AB',
    },
    divider: '#E2E8F0',
    gold: {
      main:        '#C9A86C',
      light:       '#E8CFA0',
      dark:        '#A08040',
      contrastText: '#FFFFFF',
    },
  },

  typography: {
    fontFamily: "'Pretendard', 'Apple SD Gothic Neo', -apple-system, sans-serif",
    fontSize: 16,

    h1: {
      fontFamily: "'Playfair Display', Georgia, serif",
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.2,
      letterSpacing: '-0.3px',
    },
    h2: {
      fontFamily: "'Playfair Display', Georgia, serif",
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: 1.25,
    },
    h3: {
      fontFamily: "'Playfair Display', Georgia, serif",
      fontWeight: 700,
      fontSize: '1.375rem',
      lineHeight: 1.3,
    },
    h4: {
      fontFamily: "'Playfair Display', Georgia, serif",
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h5: {
      fontFamily: "'Playfair Display', Georgia, serif",
      fontWeight: 600,
      fontSize: '1.125rem',
    },
    h6: {
      fontFamily: "'Playfair Display', Georgia, serif",
      fontWeight: 600,
      fontSize: '1rem',
    },
    body1: {
      fontSize: '1.125rem',
      lineHeight: 1.7,
    },
    body2: {
      fontSize: '0.9375rem',
      lineHeight: 1.6,
      color: '#4A5A72',
    },
    caption: {
      fontSize: '0.75rem',
      color: '#8896AB',
    },
    button: {
      fontFamily: "'Pretendard', sans-serif",
      fontWeight: 600,
      fontSize: '0.875rem',
      textTransform: 'none',
      letterSpacing: '-0.1px',
    },
    overline: {
      fontFamily: "'Pretendard', sans-serif",
      fontSize: '0.6875rem',
      fontWeight: 600,
      letterSpacing: '2px',
      textTransform: 'uppercase',
    },
  },

  shape: {
    borderRadius: 12,
  },

  shadows: [
    'none',
    '0 1px 4px rgba(13,31,60,0.06)',
    '0 2px 12px rgba(13,31,60,0.07)',
    '0 3px 20px rgba(13,31,60,0.08)',
    '0 4px 28px rgba(13,31,60,0.10)',
    '0 8px 32px rgba(13,31,60,0.12)',
    '0 12px 40px rgba(13,31,60,0.14)',
    '0 16px 48px rgba(13,31,60,0.16)',
    '0 16px 48px rgba(13,31,60,0.18)',
    '0 16px 48px rgba(13,31,60,0.20)',
    '0 16px 48px rgba(13,31,60,0.22)',
    '0 16px 48px rgba(13,31,60,0.24)',
    '0 16px 48px rgba(13,31,60,0.26)',
    '0 16px 48px rgba(13,31,60,0.28)',
    '0 16px 48px rgba(13,31,60,0.30)',
    '0 16px 48px rgba(13,31,60,0.32)',
    '0 16px 48px rgba(13,31,60,0.34)',
    '0 16px 48px rgba(13,31,60,0.36)',
    '0 16px 48px rgba(13,31,60,0.38)',
    '0 16px 48px rgba(13,31,60,0.40)',
    '0 16px 48px rgba(13,31,60,0.42)',
    '0 16px 48px rgba(13,31,60,0.44)',
    '0 16px 48px rgba(13,31,60,0.46)',
    '0 16px 48px rgba(13,31,60,0.48)',
    '0 16px 48px rgba(13,31,60,0.50)',
  ] as any,

  components: {
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: '#0D1F3C',
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: '52px',
          borderRadius: '10px',
          fontFamily: "'Pretendard', sans-serif",
          fontWeight: 600,
          fontSize: '0.9375rem',
          textTransform: 'none',
          transition: 'all 0.15s ease',
        },
        contained: {
          '&.MuiButton-containedPrimary': {
            backgroundColor: '#0D1F3C',
            '&:hover': { backgroundColor: '#0A1830' },
          },
          '&.MuiButton-containedSecondary': {
            backgroundColor: '#C9A86C',
            color: '#FFFFFF',
            '&:hover': { backgroundColor: '#B09050' },
          },
        },
        outlined: {
          '&.MuiButton-outlinedPrimary': {
            borderColor: '#D4BFA0',
            color: '#8A6A40',
            '&:hover': {
              borderColor: '#C9A86C',
              backgroundColor: '#FAF7F0',
            },
          },
          '&.MuiButton-outlinedSecondary': {
            borderColor: '#E2E8F0',
            color: '#4A5A72',
            '&:hover': {
              borderColor: '#0D1F3C',
            },
          },
        },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 2 },
      styleOverrides: {
        root: {
          borderRadius: '18px',
          border: '1px solid #E2E8F0',
          boxShadow: '0 2px 12px rgba(13,31,60,0.07)',
          transition: 'box-shadow 0.2s, transform 0.2s',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(13,31,60,0.10)',
          },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: "'Pretendard', sans-serif",
          fontWeight: 600,
          fontSize: '0.75rem',
          height: '28px',
          borderRadius: '6px',
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '18px',
          backgroundImage: 'none',
        },
      },
    },

    MuiTextField: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: {
          '& .MuiInputBase-root': {
            minHeight: '52px',
            fontSize: '1rem',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E2E8F0',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0D1F3C',
          },
          '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0D1F3C',
          },
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 4,
          borderRadius: 2,
          backgroundColor: '#E2E8F0',
        },
        bar: {
          background: 'linear-gradient(90deg, #1A3360, #C9A86C)',
          borderRadius: 2,
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: '12px',
          fontSize: '0.9375rem',
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '20px',
          padding: '8px',
        },
      },
    },
  },
});
